import { Page } from "components/Page";
import Input from "components/shared/inputs/input";
import { useEffect } from "react";
import { useGetSystemUserQuery, useUpdateSystemUserMutation } from "store/api";
import { useAuth } from "utils/hooks/useAuth";
import Button from "components/shared/button";
import { Controller } from "react-hook-form";
import useTranslation from "utils/hooks/useTranslation";
import CustomUpload from "components/shared/customUpload";
import useCustomForm from "utils/hooks/useCustomForm";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import Loader from "components/shared/loader";

export const ProfilePage: React.FC = () => {
  const { logout, setProfile } = useAuth();
  const { t } = useTranslation();

  const { data: SystemUserData, isLoading } = useGetSystemUserQuery({});

  const phoneRegExp = /^(\+)?[1-9]\d{8,15}$/;
  const emailRegExp = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const schema = yup
    .object({
      email: yup.string().required(t("required")),
      firstName: yup.string().required(t("required")),
      lastName: yup.string().required(t("required")),
      contact: yup
        .string()
        .nullable()
        .notRequired()
        .test("contact", t("should_be_an_email", null, "message"), (value) => {
          if (!value || value.trim() === "") return true; // allow empty
          return emailRegExp.test(value);
        }),
      address_postale: yup.string(),
      code_postal: yup.string(),
      ville: yup.string(),
      societe: yup.string().required(t("required")),
      telephone: yup
        .string()
        .nullable()
        .notRequired()
        .test(
          "phone",
          t("should_be_a_phone_number", null, "message"),
          (value) => {
            if (!value || value.trim() === "") return true; // allow empty
            return phoneRegExp.test(value); // validate only if not empty
          }
        ),
    })
    .required();

  const {
    //register,
    handleSubmit,
    reset,
    formState: { isDirty, errors },
    setValue,
    watch,
    control,
  } = useCustomForm<SystemUser>({
    defaultValues: SystemUserData,
    resolver: yupResolver(schema) as any,
  });

  const [updateSystemUser, { isSuccess }] = useUpdateSystemUserMutation();

  useEffect(() => {
    if (SystemUserData) {
      const normalizedData = {
        ...SystemUserData,
        contact:
          SystemUserData.contact && SystemUserData.contact.trim() !== ""
            ? SystemUserData.contact
            : SystemUserData.email, // fallback to email
      };

      reset(normalizedData);
    }
  }, [SystemUserData]);

  const handleUpdate = async (data: any) => {
    const systemUser: SystemUser = data;
    reset(data);

    return updateSystemUser({ systemUser }).then((result: any) => {
      if (result.error) {
        throw new Error(result.error.data.title);
      }

      setProfile({
        contact:
          result.data.contact && result.data.contact.trim() !== ""
            ? result.data.contact
            : result.data.email,
        firstName: result.data.firstName,
        lastName: result.data.lastName,
        email: result.data.email,
      });
    });
  };

  const onError = () => {
    // Log validation errors when form submission fails
    console.log("Validation errors:", errors);
  };

  return (
    <Page title={t("FO_COMP_MY_SPACE")}>
      <group data-align="center" data-direction="column">
        <group data-space-vertical="30" data-max-length="1440">
          <form onSubmit={handleSubmit(handleUpdate)} data-type="group">
            {isLoading ? (
              <Loader />
            ) : (
              <group data-space="20" data-length="autofit" data-gap="20">
                <group
                  data-border=""
                  data-radius="extra-special"
                  data-space="25"
                  data-gap="20"
                  direction="column"
                  data-align="start"
                  //data-length="800px"
                  data-length="autofit"
                  data-background="main-background"
                >
                  <group>
                    <text data-text-size="22" data-color="grey-color-dark">
                      {t("FO_COMP_ACCOUNT_INFO")}
                    </text>
                  </group>
                  <group data-gap="20">
                    <group data-gap="30">
                      <Input
                        dataLength="400"
                        labelSize={12}
                        label={`${t("FO_COMP_FIRSTNAME")}*`}
                        control={control}
                        name="firstName"
                        type="text"
                      />
                      <Input
                        dataLength="400"
                        labelSize={12}
                        label={t("FO_COMP_PHONENUMBER")}
                        control={control}
                        type="text"
                        name="telephone"
                      />
                    </group>
                    <group data-gap="30">
                      <Input
                        dataLength="400"
                        labelSize={12}
                        label={`${t("FO_COMP_NAME")}*`}
                        control={control}
                        type="text"
                        name="lastName"
                      />
                      <Input
                        dataLength="400"
                        labelSize={12}
                        label={t("FO_COMP_ADRESS")}
                        control={control}
                        type="text"
                        name="address_postale"
                      />
                    </group>
                    <group data-gap="30">
                      <Input
                        dataLength="400"
                        labelSize={12}
                        label={`${t("FO_COMP_COMPANY")}*`}
                        control={control}
                        type="text"
                        name="societe"
                      />
                      <Input
                        dataLength="400"
                        labelSize={12}
                        label={t("FO_COMP_CP")}
                        control={control}
                        type="text"
                        name="code_postal"
                      />
                    </group>
                    <group data-gap="30">
                      <Input
                        dataLength="400"
                        labelSize={12}
                        label={`${t("FO_COMP_EMAIL")}*`}
                        control={control}
                        type="text"
                        name="email"
                        disabled
                      />
                      <Input
                        dataLength="400"
                        labelSize={12}
                        label={t("FO_COMP_CITY")}
                        control={control}
                        type="text"
                        name="ville"
                      />
                    </group>
                    <group data-gap="30" data-max-length="400">
                      <Input
                        labelSize={12}
                        label={t("FO_COMP_EMAIL_BIS")}
                        control={control}
                        type="text"
                        name="contact"
                      />
                    </group>

                    <group
                      //data-space="10"
                      data-align="center"
                      data-justify="center"
                    >
                      <text data-color="grey-color-mid" data-wrap="wrap">
                        {t("FO_COMP_PERSONAL_INFO")}
                      </text>
                    </group>
                  </group>
                </group>

                <group
                  data-border=""
                  data-radius="extra-special"
                  data-gap="20"
                  data-space="20"
                  data-space-vertical="50"
                  data-max-length="460"
                  data-length="autofit"
                  data-type="group"
                  data-justify="start"
                  data-align="center"
                  data-direction="column"
                >
                  {SystemUserData && (
                    <Controller
                      name="images"
                      control={control}
                      render={() => (
                        <CustomUpload
                          type={"user"}
                          dataItem={SystemUserData}
                          imageField="images"
                          setValue={setValue}
                          watch={watch}
                        />
                      )}
                    />
                  )}
                </group>
              </group>
            )}

            <group data-space="15" data-gap="10">
              <Button
                large
                accent
                text={t("FO_COMP_TO_DISCONNECT")}
                icon="logout"
                onClick={logout}
              />{" "}
              {isSuccess && !isDirty ? (
                <Button
                  accent_text
                  large
                  text={t("FO_COMP_SAVE_MODIF")}
                  disabled={!isDirty}
                  icon="done"
                  onClick={() => {
                    handleSubmit(handleUpdate, onError)();
                  }}
                />
              ) : (
                <Button
                  primary
                  large
                  text={t("FO_COMP_SAVE_MODIF")}
                  disabled={!isDirty}
                  icon="save"
                  onClick={() => {
                    handleSubmit(handleUpdate, onError)();
                  }}
                />
              )}
            </group>
          </form>
        </group>
      </group>
    </Page>
  );
};
